import { memo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Loader2 } from 'lucide-react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useOrganizationSwitcher } from '../../../hooks/organizations/useOrganizationSwitcher';
import { apolloClient } from '../../../lib/apolloClient';
import { useQueryClient } from '@tanstack/react-query';
import { useAskRemiStore } from '../../../stores/useAskRemiStore';

export const OrganizationSwitcher = memo(function OrganizationSwitcher() {
  const { organization, setOrganization, fetchOrganization } = useOrganizationStore();
  const {
    data: organizations = [],
    isLoading,
    search,
    setSearch,
    setPage,
    hasNext,
  } = useOrganizationSwitcher();
  const queryClient = useQueryClient();
  const clearMessages = useAskRemiStore((s) => s.clearMessages);

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position dropdown — flips above when space is tight, clamps within viewport
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const margin = 8;
    const dropdownMaxH = 320;

    const minWidth = 260;
    const desiredWidth = Math.max(rect.width, minWidth);
    const maxWidth = viewportW - margin * 2;
    const width = Math.min(desiredWidth, maxWidth);

    let left = rect.left;
    if (left + width > viewportW - margin) left = viewportW - width - margin;
    if (left < margin) left = margin;

    const spaceBelow = viewportH - rect.bottom;
    const above = spaceBelow < dropdownMaxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      ...(above ? { bottom: viewportH - rect.top + 4 } : { top: rect.bottom + 4 }),
      left,
      width,
      zIndex: 9999,
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Position before paint so the dropdown never flashes at the bottom of
  // the page (it's portaled to <body>; without `position: fixed` it would
  // briefly land in document flow there).
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // Focus search + reset query on open/close
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [open, setSearch]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Infinite scroll — load next page when near bottom
  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasNext || isLoading) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      setPage((p) => p + 1);
    }
  }, [hasNext, isLoading, setPage]);

  const handleSelect = useCallback(
    (id: string) => {
      const selectedOrg = organizations.find((org) => org.id === id);
      if (!selectedOrg) return;
      // Cache the new org ID so fetchOrganization knows which org to load,
      // then show a loading spinner until the full org object is fetched.
      setOrganization(selectedOrg as Parameters<typeof setOrganization>[0]);
      useOrganizationStore.setState({ loading: true });
      clearMessages();
      queryClient.clear();
      apolloClient.clearStore().then(() => fetchOrganization());
      setOpen(false);
    },
    [organizations, setOrganization, clearMessages, queryClient, fetchOrganization]
  );

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-background border border-foreground/20 rounded-lg shadow-lg overflow-hidden"
    >
      {/* Search */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-foreground/10">
        <Search className="w-3 h-3 text-muted-foreground shrink-0" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search organizations..."
          className="flex-1 text-xs bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {isLoading && (
          <Loader2 className="w-3 h-3 text-muted-foreground animate-spin shrink-0" />
        )}
      </div>

      {/* Scrollable list */}
      <ul
        ref={listRef}
        onScroll={handleListScroll}
        className={`max-h-64 overflow-y-auto ${organizations.length === 0 && !isLoading ? '' : 'py-1'}`}
      >
        {organizations.length === 0 && !isLoading && (
          <li className="px-3 py-1.5 text-xs text-muted-foreground">No results</li>
        )}
        {organizations.length === 0 && isLoading && (
          <li className="flex justify-center py-2.5">
            <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
          </li>
        )}
        {organizations.map((org) => (
          <li key={org.id}>
            <button
              type="button"
              onClick={() => handleSelect(org.id)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/60 transition-colors truncate ${
                organization?.id === org.id
                  ? 'text-primaryfortext font-medium bg-muted/30'
                  : 'text-foreground'
              }`}
            >
              {org.org_name}
            </button>
          </li>
        ))}
        {isLoading && organizations.length > 0 && (
          <li className="flex justify-center py-1.5">
            <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
          </li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className="relative w-full lg:min-w-[320px]">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-foreground/30 rounded-lg bg-background hover:bg-muted/50 transition-colors text-left"
      >
        <span className="truncate text-foreground">
          {organization?.org_name || <span className="text-muted-foreground">Login as</span>}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {typeof document !== 'undefined' && createPortal(dropdown, document.body)}
    </div>
  );
});
